import Box from '@mui/material/Box';
import { ButtonProps } from '@mui/material/Button';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppBar from '@mui/material/AppBar';
import DisplayHeadingWithRule from 'src/common/components/DisplayHeadingWithRule';
import DashboardCollapsibleHeaderActions from 'src/common/components/dashboard/DashboardCollapsibleHeaderActions';
import {
  dashboardPageHeaderSx,
  dashboardPageHeaderTitleSlotSx,
  dashboardPageHeaderTitleTypographySx,
} from 'src/common/components/dashboard/dashboardPageHeaderStyles';

import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';

export {
  dashboardPageHeaderActionsSx,
  
  dashboardPageHeaderSx,
  dashboardPageHeaderTitleSlotSx,
  dashboardPageHeaderTitleTypographySx,
} from 'src/common/components/dashboard/dashboardPageHeaderStyles';

const dashboardToolbarPaperSx = {
  ...frostedPanelSx(0.5),
  mb: { xs: 1, sm: 2 },
  mt: { xs: 0, sm: 2 },
  px: 2,
  py: { xs: 1, sm: 1.5 },
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
} as const;

const dashboardToolbarPaperStickySx = {
  ...frostedPanelSx(0.5),
  mb: 0,
  mt: 0,
  px: 2,
  py: { xs: 1, sm: 1.5 },
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
} as const;

const dashboardToolbarGridContainerSx = {
  alignItems: 'center',
  flex: 1,
  flexWrap: 'wrap',
  width: '100%',
} as const;

const dashboardToolbarColumnSpacing = { xs: 1.5, sm: 2, md: 3 } as const;
const dashboardToolbarRowSpacing = 2;

/** Shared props for primary header actions (Add, New, Upload, etc.). */
export function dashboardPrimaryActionButtonProps(): Pick<
  ButtonProps,
  'size' | 'variant' | 'color' | 'sx'
> {
  return {
    size: 'small',
    variant: 'contained',
    color: 'primary',
    sx: { flexShrink: 0 },
  };
}

type DashboardPageHeaderProps = {
  title: ReactNode;
  actions?: ReactNode;
  actionsAriaLabel?: string;
};

/** Page title with optional actions aligned to the right on the same row. */
export function DashboardPageHeader({ title, actions, actionsAriaLabel }: DashboardPageHeaderProps) {
  const { t } = useTranslation();
  const resolvedActionsAriaLabel = actionsAriaLabel ?? t('dashboard.pageActions');

  const titleNode =
    typeof title === 'string' ? (
      <DisplayHeadingWithRule
        variant="h2"
        component="h2"
        sx={dashboardPageHeaderTitleSlotSx}
        typographySx={dashboardPageHeaderTitleTypographySx}
      >
        {title}
      </DisplayHeadingWithRule>
    ) : (
      <Box sx={dashboardPageHeaderTitleSlotSx}>{title}</Box>
    );

  return (
    <Box sx={dashboardPageHeaderSx}>
      {titleNode}
      {actions ? (
        <DashboardCollapsibleHeaderActions ariaLabel={resolvedActionsAriaLabel}>
          {actions}
        </DashboardCollapsibleHeaderActions>
      ) : null}
    </Box>
  );
}

type DashboardPageToolbarProps = {
  children: ReactNode;
  /** Margins are owned by the sticky shell in dashboard page layouts. */
  edge?: 'default' | 'sticky';
  /**
   * When true (default), collapses to a "Search & Filters" dialog below `md`.
   * Set false for section toolbars that should stay expanded on mobile.
   */
  collapseOnMobile?: boolean;
};

/** AppBar + frosted Paper toolbar with a responsive Grid layout for dashboard list pages. */
export function DashboardPageToolbar({
  children,
  edge = 'default',
  collapseOnMobile = true,
}: DashboardPageToolbarProps) {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const paperSx = edge === 'sticky' ? dashboardToolbarPaperStickySx : dashboardToolbarPaperSx;

  if (isWide || !collapseOnMobile) {
    return (
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar
          component={Paper}
          sx={[paperSx, { flexWrap: 'wrap', minHeight: 'auto', gap: 1.5 }]}
        >
          <Grid
            container
            rowSpacing={dashboardToolbarRowSpacing}
            columnSpacing={dashboardToolbarColumnSpacing}
            sx={dashboardToolbarGridContainerSx}
          >
            {children}
          </Grid>
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar
          component={Paper}
          sx={[paperSx, { flexWrap: 'nowrap', minHeight: 'auto' }]}
        >
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<TuneIcon />}
            onClick={() => setMobileOpen(true)}
            aria-haspopup="dialog"
          >
            {t('dashboard.searchAndFilters')}
          </Button>
        </Toolbar>
      </AppBar>
      <Dialog open={mobileOpen} onClose={() => setMobileOpen(false)} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          {t('dashboard.searchAndFilters')}
          <IconButton aria-label={t('base.close')} onClick={() => setMobileOpen(false)} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid
            container
            rowSpacing={dashboardToolbarRowSpacing}
            columnSpacing={dashboardToolbarColumnSpacing}
            sx={dashboardToolbarGridContainerSx}
          >
            {children}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}

type DashboardToolbarSlotProps = {
  children: ReactNode;
};

/** Primary actions (add, delete-all, etc.): full width on xs, inline from md. */
export function DashboardToolbarActions({ children }: DashboardToolbarSlotProps) {
  return (
    <Grid
      size={{ xs: 12, md: 'auto' }}
      sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}
    >
      {children}
    </Grid>
  );
}

/** Page section title inside the toolbar (profile, check-in header, etc.). */
export function DashboardToolbarHeading({ children }: DashboardToolbarSlotProps) {
  return (
    <Grid size={{ xs: 12, md: 'auto' }} sx={{ flexShrink: 0, minWidth: 0 }}>
      {children}
    </Grid>
  );
}

type DashboardToolbarAsideProps = DashboardToolbarSlotProps & {
  /** `end`: view switch aligned right on large screens; `grow`: filters fill remaining width. */
  variant?: 'end' | 'grow' | 'full';
};

/** Secondary toolbar content: filters, view switch, or full-width blocks. */
export function DashboardToolbarAside({ children, variant = 'end' }: DashboardToolbarAsideProps) {
  if (variant === 'full') {
    return (
      <Grid size={{ xs: 12 }} sx={{ minWidth: 0 }}>
        {children}
      </Grid>
    );
  }

  if (variant === 'grow') {
    return (
      <Grid size={{ xs: 12, lg: 'grow' }} sx={{ minWidth: 0 }}>
        {children}
      </Grid>
    );
  }

  return (
    <Grid
      size={{ xs: 12, sm: 'auto', lg: 'grow' }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { lg: 'flex-end' },
        minWidth: 0,
      }}
    >
      {children}
    </Grid>
  );
}
